import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { CheapestAvailabilityRow } from '../api/types';
import { count } from '../utils/format';

import { AxisTick } from './AxisTick';
import { ChartTooltip } from './ChartTooltip';
import { Legend } from './Legend';
import { CHROME, DIVERGE, STATUS } from './palette';

const BEHIND = DIVERGE.high;
const AHEAD = STATUS.good;

/**
 * What the cheapest rival offer claims about its own stock, split by whether it
 * currently undercuts us. The wording is Heureka's own — an "in stock" taxonomy
 * invented here would quietly reclassify shops.
 */
export function AvailabilitySplitChart({ rows }: { rows: CheapestAvailabilityRow[] }) {
  const data = rows.map((row) => ({
    ...row,
    weAreAhead: Math.max(0, row.products - row.weAreBehind),
  }));

  if (data.length === 0) {
    return <div className="state">No competing offers on the newest scans.</div>;
  }

  return (
    <>
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 34 + 48)}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 40, bottom: 4, left: 4 }} barCategoryGap={6}>
          <CartesianGrid stroke={CHROME.grid} horizontal={false} />
          <XAxis type="number" tick={{ fill: CHROME.inkMuted, fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis
            type="category"
            dataKey="availability"
            axisLine={false}
            tickLine={false}
            width={150}
            tick={(props) => <AxisTick {...props} max={22} />}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) {
                return null;
              }

              const row = payload[0].payload as CheapestAvailabilityRow & { weAreAhead: number };

              return (
                <ChartTooltip
                  title={row.availability}
                  note="stated by the cheapest competing offer"
                  rows={[
                    { key: 'behind', label: 'They undercut you', value: count(row.weAreBehind), color: BEHIND },
                    { key: 'ahead', label: 'You are still ahead', value: count(row.weAreAhead), color: AHEAD },
                    { key: 'all', label: 'Products', value: count(row.products) },
                  ]}
                />
              );
            }}
          />
          <Bar isAnimationActive={false} dataKey="weAreBehind" stackId="stock" fill={BEHIND} stroke={CHROME.surface} strokeWidth={2} maxBarSize={24} />
          <Bar isAnimationActive={false} dataKey="weAreAhead" stackId="stock" fill={AHEAD} stroke={CHROME.surface} strokeWidth={2} maxBarSize={24} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>

      <Legend
        entries={[
          { label: 'They undercut you', color: BEHIND },
          { label: 'You are still ahead', color: AHEAD },
        ]}
      />
    </>
  );
}
